import type {
  AnimPlayActionNode,
  AnimationDirection,
  AnimationFillMode,
} from "../../types.js";
import {
  BehaviorFlow,
  BehaviorAnimationPayload,
  ExecutionContext,
  NodeExecutionResult,
  PatchResult,
  RuntimeSignal,
  ScheduledNode,
} from "../types.js";
import {
  coerceCssAnimationDirection,
  coerceCssAnimationFillMode,
  emptyPatchResult,
  toFiniteNumber,
} from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

/**
 * Plays a named scene animation on the current node.
 */
export class AnimPlayBehaviorNode extends BehaviorNode {
  declare behavior: AnimPlayActionNode;

  constructor(behavior: AnimPlayActionNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    const patch: PatchResult = emptyPatchResult();
    const visiting = new Set<string>();
    const scheduledNodes: ScheduledNode[] = [];
    let nextFlows: BehaviorFlow[] = [];
    const rawName = this.getInput("name", visiting, context);
    const name = String(rawName ?? "").trim();
    const sceneAnimations = context.sceneAnimationsByName ?? {};
    const basePayload = name
      ? sceneAnimations[name.toLocaleLowerCase()]
      : undefined;

    if (basePayload) {
      const durationMs = Math.max(
        0,
        toFiniteNumber(
          this.getInput("durationMs", visiting, context),
          basePayload.durationMs,
        ),
      );
      const delayMs = Math.max(
        0,
        toFiniteNumber(
          this.getInput("delayMs", visiting, context),
          basePayload.delayMs,
        ),
      );
      const iterationCount = this.behavior.playInfinite
        ? Infinity
        : Math.max(
            1,
            Math.floor(
              toFiniteNumber(
                this.getInput("iterationCount", visiting, context),
                basePayload.iterationCount,
              ),
            ),
          );
      const direction = coerceCssAnimationDirection(
        this.getInput("direction", visiting, context) ?? basePayload.direction,
      ) as AnimationDirection;
      const fillMode = coerceCssAnimationFillMode(
        this.getInput("fillMode", visiting, context) ?? basePayload.fillMode,
      ) as AnimationFillMode;

      const payload: BehaviorAnimationPayload = {
        ...basePayload,
        durationMs,
        delayMs,
        iterationCount,
        direction,
        fillMode,
      };
      patch.animation = payload;

      const waitMs = this.behavior.playInfinite
        ? 0
        : Math.max(0, delayMs + durationMs * iterationCount);
      const outEdges = this.outgoingEdgesByPort.get("out") ?? [];
      if (!this.behavior.playInfinite && outEdges.length > 0) {
        for (const edge of outEdges) {
          scheduledNodes.push({
            at: context.nowMs + waitMs,
            kind: "edge",
            edge,
            context,
          });
        }
      } else if (context.loopStack.length > 0) {
        scheduledNodes.push({
          at: context.nowMs + waitMs,
          kind: "signal",
          signal: { type: "loop_continue", context },
          context,
        });
      }
    } else {
      // If no scene animation is found, keep legacy immediate flow behavior.
      nextFlows = this.getNextFlows(context);
    }

    const runtimeSignals: RuntimeSignal[] = [];
    if (
      scheduledNodes.length === 0 &&
      nextFlows.length === 0 &&
      context.loopStack.length > 0
    ) {
      runtimeSignals.push({ type: "loop_continue", context });
    }

    return {
      patch,
      nextFlows,
      scheduledNodes,
      runtimeSignals,
    };
  }

  getOutput(
    _portId: string,
    _visiting: Set<string>,
    _context: ExecutionContext,
  ): unknown {
    return undefined;
  }

  private getNextFlows(context: ExecutionContext): BehaviorFlow[] {
    return (this.outgoingEdgesByPort.get("out") ?? []).map((edge) => ({
      edge,
      context,
    }));
  }
}
